import { Navigate, useLocation } from 'react-router-dom';

/**
 * Redirect an old Memory Engine deep link onto the unified Library.
 *
 * `<Navigate to="/memory/library" />` drops the query string, which would turn
 * every bookmarked `?selected=` link into a generic landing. That is already a
 * live bug on `/memory/impact`. This rebuilds the search instead, and maps the
 * old parameter vocabulary onto the new one:
 *
 * - `/memory/inbox` defaulted to needs-review, so a link with no `status`
 *   means `status=needs_review`.
 * - A *blank* `status=` meant "everything" and has to stay blank. Absent and
 *   blank are different, and collapsing them would silently re-filter a link
 *   somebody saved deliberately.
 * - `?kind=proposal` was the rule-changes tab, now included in Needs review.
 * - `?selected=` carried a bare id on `/memory/approved` and either a bare id
 *   or `proposal:{id}` on `/memory/inbox`; both are widened to the Library's
 *   `kind:id` selection keys.
 */
export function LegacyMemoryRedirect({ from }: { from: 'inbox' | 'approved' }) {
  const location = useLocation();
  const incoming = new URLSearchParams(location.search);
  const next = new URLSearchParams();

  const kind = incoming.get('kind') ?? '';
  const hadStatus = incoming.has('status');
  const status = incoming.get('status') ?? '';

  if (kind === 'proposal' || kind === 'catalog_proposal') {
    next.set('status', 'needs_review');
  } else if (from === 'inbox') {
    // Blank stays blank; absent takes the page's old default.
    next.set('status', hadStatus ? status : 'needs_review');
  } else if (hadStatus && status && status !== 'active') {
    // The Approved page's own status vocabulary (inactive, superseded, …) has
    // no chip of its own; those rows live under Everything.
    next.set('status', '');
  }

  const selected = incoming.get('selected') ?? '';
  if (selected) next.set('selected', widenSelection(selected, from));

  const q = incoming.get('q') ?? '';
  if (q) next.set('q', q);

  const search = next.toString();
  return <Navigate to={`/memory/library${search ? `?${search}` : ''}`} replace />;
}

function widenSelection(selected: string, from: 'inbox' | 'approved'): string {
  if (selected.startsWith('proposal:')) return selected;
  if (selected.includes(':')) return selected;
  return from === 'approved' ? `item:${selected}` : `candidate:${selected}`;
}
